"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { ApiWave } from "@/generated/models/ApiWave";
import { Time } from "@/helpers/time";
import { TimeLeft, calculateTimeLeft, calculateLastDecisionTime, FALLBACK_START_TIME, FALLBACK_END_TIME } from "@/helpers/waves/time.utils";
type PhaseState = "UPCOMING" | "IN_PROGRESS" | "COMPLETED";

/**
 * Timer results interface
 */
interface WaveTimersResult {
  // Participation time information
  participation: {
    timeLeft: TimeLeft;
    isUpcoming: boolean;
    isInProgress: boolean;
    isCompleted: boolean;
  };

  // Voting time information
  voting: {
    timeLeft: TimeLeft;
    isUpcoming: boolean;
    isInProgress: boolean;
    isCompleted: boolean;
  };

  // Decisions information
  decisions: {
    firstDecisionDone: boolean;
  };
}

// Using the shared constants from time.utils.ts

/**
 * Hook for handling timers in waves.
 *
 * This hook is designed to be used separately from useWave to control interval timers.
 * Timers are always active when this hook is used.
 *
 * @param wave The wave object
 * @returns Timer states
 */
export function useWaveTimers(wave) {
  const $ = _c(39);
  const participationStartTime = wave?.participation.period?.min ?? FALLBACK_START_TIME;
  const participationEndTime = wave?.participation.period?.max ?? FALLBACK_END_TIME;
  const votingStartTime = wave?.voting.period?.min ?? FALLBACK_START_TIME;
  let t0;
  if ($[0] !== wave) {
    t0 = wave ? calculateLastDecisionTime(wave) : FALLBACK_END_TIME;
    $[0] = wave;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const actualVotingEndTime = t0;
  const [participationPhase, setParticipationPhase] = useState("UPCOMING");
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [participationTimeLeft, setParticipationTimeLeft] = useState(t1);
  const [votingTimePhase, setVotingTimePhase] = useState("UPCOMING");
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [votingTimeLeft, setVotingTimeLeft] = useState(t2);
  const [isFirstDecisionDone, setIsFirstDecisionDone] = useState(false);
  let t3;
  if ($[4] !== participationEndTime || $[5] !== participationStartTime) {
    t3 = () => {
      const now = Time.currentMillis();
      let newPhaseState;
      if (participationStartTime > now) {
        newPhaseState = "UPCOMING";
      } else {
        if (participationEndTime < now) {
          newPhaseState = "COMPLETED";
        } else {
          newPhaseState = "IN_PROGRESS";
        }
      }
      let timeLeft = {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      };
      if (newPhaseState !== "COMPLETED") {
        const targetTime = newPhaseState === "UPCOMING" ? participationStartTime : participationEndTime;
        timeLeft = calculateTimeLeft(targetTime);
      }
      return {
        phase: newPhaseState,
        timeLeft
      };
    };
    $[4] = participationEndTime;
    $[5] = participationStartTime;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const calculateParticipationTimeState = t3;
  let t4;
  if ($[7] !== actualVotingEndTime || $[8] !== votingStartTime) {
    t4 = () => {
      const now_0 = Time.currentMillis();
      let newPhaseState_0;
      if (votingStartTime > now_0) {
        newPhaseState_0 = "UPCOMING";
      } else {
        if (actualVotingEndTime < now_0) {
          newPhaseState_0 = "COMPLETED";
        } else {
          newPhaseState_0 = "IN_PROGRESS";
        }
      }
      let timeLeft_0 = {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      };
      if (newPhaseState_0 !== "COMPLETED") {
        const targetTime_0 = newPhaseState_0 === "UPCOMING" ? votingStartTime : actualVotingEndTime;
        timeLeft_0 = calculateTimeLeft(targetTime_0);
      }
      return {
        phase: newPhaseState_0,
        timeLeft: timeLeft_0
      };
    };
    $[7] = actualVotingEndTime;
    $[8] = votingStartTime;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const calculateVotingTimeState = t4;
  let t5;
  if ($[10] !== wave?.wave.decisions_strategy?.first_decision_time) {
    t5 = () => {
      const now_1 = Time.currentMillis();
      const firstDecisionTime = wave?.wave.decisions_strategy?.first_decision_time;
      if (!firstDecisionTime) {
        return false;
      }
      return now_1 >= firstDecisionTime;
    };
    $[10] = wave?.wave.decisions_strategy?.first_decision_time;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const calculateFirstDecisionStatus = t5;
  let t6;
  if ($[12] !== calculateFirstDecisionStatus || $[13] !== calculateParticipationTimeState || $[14] !== calculateVotingTimeState || $[15] !== wave) {
    t6 = () => {
      if (!wave) {
        return;
      }
      const {
        phase: participationPhaseState,
        timeLeft: participationTimeLeftState
      } = calculateParticipationTimeState();
      setParticipationPhase(participationPhaseState);
      setParticipationTimeLeft(participationTimeLeftState);
      const {
        phase: votingPhaseState,
        timeLeft: votingTimeLeftState
      } = calculateVotingTimeState();
      setVotingTimePhase(votingPhaseState);
      setVotingTimeLeft(votingTimeLeftState);
      setIsFirstDecisionDone(calculateFirstDecisionStatus());
      const updateParticipationTime = () => {
        const {
          phase,
          timeLeft: timeLeft_1
        } = calculateParticipationTimeState();
        setParticipationPhase(phase);
        setParticipationTimeLeft(timeLeft_1);
      };
      const updateVotingTime = () => {
        const {
          phase: phase_0,
          timeLeft: timeLeft_2
        } = calculateVotingTimeState();
        setVotingTimePhase(phase_0);
        setVotingTimeLeft(timeLeft_2);
      };
      const updateFirstDecisionStatus = () => {
        const firstDecisionDone = calculateFirstDecisionStatus();
        setIsFirstDecisionDone(firstDecisionDone);
        return firstDecisionDone;
      };
      const participationTimer = setInterval(updateParticipationTime, 1000);
      const votingTimer = setInterval(updateVotingTime, 1000);
      let firstDecisionTimer = null;
      if (!calculateFirstDecisionStatus()) {
        firstDecisionTimer = setInterval(() => {
          if (updateFirstDecisionStatus()) {
            if (firstDecisionTimer) {
              clearInterval(firstDecisionTimer);
              firstDecisionTimer = null;
            }
          }
        }, 1000);
      }
      return () => {
        clearInterval(participationTimer);
        clearInterval(votingTimer);
        if (firstDecisionTimer) {
          clearInterval(firstDecisionTimer);
        }
      };
    };
    $[12] = calculateFirstDecisionStatus;
    $[13] = calculateParticipationTimeState;
    $[14] = calculateVotingTimeState;
    $[15] = wave;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== actualVotingEndTime || $[18] !== participationEndTime || $[19] !== participationStartTime || $[20] !== votingStartTime || $[21] !== wave) {
    t7 = [wave, participationStartTime, participationEndTime, votingStartTime, actualVotingEndTime];
    $[17] = actualVotingEndTime;
    $[18] = participationEndTime;
    $[19] = participationStartTime;
    $[20] = votingStartTime;
    $[21] = wave;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  useEffect(t6, t7);
  const t8 = participationPhase === "UPCOMING";
  const t9 = participationPhase === "IN_PROGRESS";
  const t10 = participationPhase === "COMPLETED";
  let t11;
  if ($[23] !== participationTimeLeft || $[24] !== t10 || $[25] !== t8 || $[26] !== t9) {
    t11 = {
      timeLeft: participationTimeLeft,
      isUpcoming: t8,
      isInProgress: t9,
      isCompleted: t10
    };
    $[23] = participationTimeLeft;
    $[24] = t10;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = votingTimePhase === "UPCOMING";
  const t13 = votingTimePhase === "IN_PROGRESS";
  const t14 = votingTimePhase === "COMPLETED";
  let t15;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t14 || $[31] !== votingTimeLeft) {
    t15 = {
      timeLeft: votingTimeLeft,
      isUpcoming: t12,
      isInProgress: t13,
      isCompleted: t14
    };
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = votingTimeLeft;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== isFirstDecisionDone) {
    t16 = {
      firstDecisionDone: isFirstDecisionDone
    };
    $[33] = isFirstDecisionDone;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t11 || $[36] !== t15 || $[37] !== t16) {
    t17 = {
      participation: t11,
      voting: t15,
      decisions: t16
    };
    $[35] = t11;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
}